import {
  Button,
  Chip as MuiChip,
  Stack,
  TableCell,
  Typography,
  styled,
  useTheme,
} from "@mui/material"
import { LoadingButton } from "@pharmupp/p3-front-commons"
import { useParams } from "react-router-dom"
import {
  type ApiListAdhMarketingOperation,
  OperationStateEnum,
} from "../../AdherentMarketingOperations"
import { useSubscriptionApi } from "../../AdherentMarketingOperations/AdhMarketingOPDisplay/useAdhMarketingOPDisplayApi"
import { HiddenIconBtn } from "../../AdherentMarketingOperations/common/components/HiddenBtn"

const JustifyButton = ({
  label,
  disabled,
}: {
  label: string
  disabled?: boolean
}) => (
  <Button variant="outlined" size="small" color="primary" disabled={disabled}>
    {label}
  </Button>
)

const SubscribeButton = ({
  operationId,
  label,
  disabled = false,
  hidden = false,
}: {
  operationId: number
  label: string
  disabled?: boolean
  hidden?: boolean
}) => {
  const { id: adherentId } = useParams()
  const { subscribe, isSubscribing } = useSubscriptionApi({
    operationId: String(operationId),
    adherentId,
  })

  return (
    <LoadingButton
      variant={hidden ? "outlined" : "contained"}
      size="small"
      sx={{
        flex: 1,
        color: hidden ? "primary.main" : "common.white",
      }}
      onClick={(e) => {
        e.stopPropagation()
        subscribe?.()
      }}
      loading={isSubscribing}
      disabled={disabled}
    >
      {label}
    </LoadingButton>
  )
}

const ValidatedChip = () => (
  <MuiChip
    label="Validée"
    variant="filled"
    size="small"
    color="success"
    sx={{ color: "white" }}
  />
)

type AdhStatusCellProps = Pick<
  ApiListAdhMarketingOperation,
  | "id"
  | "state"
  | "kitOrderStartDate"
  | "kitOrderEndDate"
  | "validityStartDate"
  | "validityEndDate"
  | "hidden"
>

const Cta = ({
  id,
  state,
  kitOrderStartDate,
  kitOrderEndDate,
  validityStartDate,
  validityEndDate,
  hidden,
}: AdhStatusCellProps) => {
  const { typography } = useTheme()

  switch (state) {
    case OperationStateEnum.REGISTRABLE:
      return (
        <Stack spacing={0.5}>
          <LayoutStatusBtn>
            <SubscribeButton operationId={id} label="S'inscrire" hidden={hidden} />
            <HiddenIconBtn hidden={hidden} size="small" />
          </LayoutStatusBtn>
          <Typography color="grey.light" fontSize={typography.pxToRem(10)}>
            Avant le {kitOrderEndDate}
          </Typography>
        </Stack>
      )
    case OperationStateEnum.UPCOMING:
      return (
        <Stack spacing={0.5}>
          <LayoutStatusBtn>
            <SubscribeButton operationId={id} label="S'inscrire" disabled />
            <HiddenIconBtn hidden={hidden} size="small" />
          </LayoutStatusBtn>
          <Typography color="grey.light" fontSize={typography.pxToRem(10)}>
            À partir du {kitOrderStartDate}
          </Typography>
        </Stack>
      )
    case OperationStateEnum.WAITING:
      return (
        <Stack spacing={0.5}>
          <JustifyButton label="Justifier" disabled />
          <Typography color="grey.light" fontSize={typography.pxToRem(10)}>
            À partir du {validityStartDate}
          </Typography>
        </Stack>
      )
    case OperationStateEnum.REVIEWED:
      return (
        <Stack spacing={0.5}>
          <JustifyButton label="Justifier" disabled />
          <Typography color="grey.light" fontSize={typography.pxToRem(10)}>
            Jusqu’au {validityEndDate}
          </Typography>
        </Stack>
      )

    case OperationStateEnum.STARTED:
      return (
        <Stack spacing={0.5}>
          <JustifyButton label="Justifier" />
          <Typography color="grey.light" fontSize={typography.pxToRem(10)}>
            Jusqu’au {validityEndDate}
          </Typography>
        </Stack>
      )
    case OperationStateEnum.REJECTED:
      return (
        <Stack spacing={0.5}>
          <JustifyButton label="Justifier à nouveau" />
          <Typography color="grey.light" fontSize={typography.pxToRem(10)}>
            Jusqu’au {validityEndDate}
          </Typography>
        </Stack>
      )
    case OperationStateEnum.VALIDATED:
      return <ValidatedChip />
    default:
      return null
  }
}

export const AdhStatusCell = (props: AdhStatusCellProps) => {
  return (
    <TableCell align="right">
      <Cta {...props} />
    </TableCell>
  )
}

const LayoutStatusBtn = styled(Stack)(({ theme: { spacing } }) => ({
  flexDirection: "row",
  alignItems: "center",
  width: "100%",
  position: "relative",
  gap: spacing(0.5),
}))
